import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ChartLineUp, Upload, Share, Notepad, Lightning, CheckCircle, Camera, MagnifyingGlass, Star, TrendUp, TrendDown, CheckSquare, Calendar as CalendarIcon, Trophy, Target, Coins, Ranking, LockKey, MegaphoneSimple, Scales, Rocket, GraduationCap, Plugs, FileArrowDown } from "@phosphor-icons/react";
import CinematicHero from "../components/CinematicHero";
import CursorSparkles from "../components/CursorSparkles";
import HeroAnimatedChart from "../components/HeroAnimatedChart";
import GoogleSignInButton from "../components/GoogleSignInButton";
import SectionNav from "../components/SectionNav";
import useReveal from "../lib/useReveal";
import useParallax from "../lib/useParallax";
import useSeo from "../lib/useSeo";
import { getRegionSeo, applyRegionCurrency, faqJsonLd, howToPassChallengeJsonLd, productJsonLd, organizationJsonLd } from "../lib/geoSeo";
import useCurrency from "../lib/useCurrency";
import { formatPrice } from "../lib/pricingCurrency";
import CurrencyPicker from "../components/CurrencyPicker";
import { useRef } from "react";

// Base pricing lives in INR (Razorpay's native currency). The landing page
// shows an approximate conversion via `formatPrice(inr, ccy)` — see
// `lib/pricingCurrency.js`. Save amounts are computed off the base numbers
// so they stay in sync when currency changes.
const plans = [
  { id: "monthly",   name: "Monthly",   inr: 600,   savingsInr: 0,    per: "per month",     pop: false, features: ["Unlimited trades", "CSV import", "MT4 / MT5 statement import", "All analytics", "Chart uploads"] },
  { id: "quarterly", name: "Quarterly", inr: 1500,  savingsInr: 300,  per: "per 3 months",  pop: true,  features: ["Everything in Monthly", "Auto broker sync (Binance / CoinDCX / Delta)", "Priority support", "Sharable P&L links"] },
  { id: "yearly",    name: "Yearly",    inr: 4500,  savingsInr: 2700, per: "per year",      pop: false, features: ["Everything in Quarterly", "PDF reports", "Nightly auto-journaling", "Multi-account access"] },
];

function Cell({ v, highlight = false }) {
  if (v === "yes") return (
    <CheckCircle size={20} weight="fill" className={`inline-block text-emerald-500 ${highlight ? "drop-shadow-[0_0_6px_rgba(16,185,129,0.6)]" : ""}`} />
  );
  if (v === "partial") return (
    <span className="inline-block text-amber-500 font-bold text-lg" title="Limited">◐</span>
  );
  return <span className="inline-block text-neutral-300 font-bold text-lg" title="Not available">—</span>;
}

/**
 * Marketing preview of the in-app P&L Calendar.
 * Auto-tracks the visitor's current month/year with the correct first-of-month
 * weekday alignment and number of days. Daily P&L is a deterministic sample
 * derived from year+month+day so the visual reads well but stays static per
 * visit — real users see their own trades once they sign up.
 */
function PnlCalendarShowcase() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();                          // 0..11
  const monthName = now.toLocaleString("en-US", { month: "long", year: "numeric" });
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Convert Sun-first weekday to Mon-first: 0=Mon..6=Sun
  const firstMonIdx = (new Date(year, month, 1).getDay() + 6) % 7;
  const totalCells = Math.ceil((firstMonIdx + daysInMonth) / 7) * 7;

  // Deterministic pseudo-random for stable P&L per (y,m,d)
  const seeded = (d) => {
    const x = Math.sin(year * 1000 + month * 100 + d) * 10000;
    return x - Math.floor(x);              // 0..1
  };

  // Build cell values: undefined = out-of-month; null = weekend; number = P&L
  const days = Array.from({ length: totalCells }, (_, i) => {
    const dayNum = i - firstMonIdx + 1;
    if (dayNum < 1 || dayNum > daysInMonth) return undefined;
    const dow = (i % 7);                    // 0..5=Mon..Sat, 6=Sun (already Mon-first)
    if (dow >= 5) return null;              // Sat / Sun
    // Deterministic P&L: 55% chance winning, magnitude 40..760
    const r = seeded(dayNum);
    const sign = r > 0.42 ? 1 : -1;
    const magnitude = Math.round(40 + seeded(dayNum + 999) * 720);
    return sign * magnitude;
  });

  const dayNumFor = (i) => {
    const d = i - firstMonIdx + 1;
    return d >= 1 && d <= daysInMonth ? d : null;
  };

  const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const total = days.reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
  const wins = days.filter(v => typeof v === "number" && v > 0).length;
  const losses = days.filter(v => typeof v === "number" && v < 0).length;

  // Tone: intensity based on magnitude
  const toneClass = (v) => {
    if (v == null || v === undefined) return "";
    const a = Math.abs(v);
    const level = a > 500 ? 3 : a > 250 ? 2 : 1;
    if (v > 0) return ["bg-emerald-50 text-emerald-700 border-emerald-200",
                       "bg-emerald-100 text-emerald-800 border-emerald-300",
                       "bg-emerald-500 text-white border-emerald-600"][level - 1];
    return   ["bg-red-50 text-red-700 border-red-200",
              "bg-red-100 text-red-800 border-red-300",
              "bg-red-500 text-white border-red-600"][level - 1];
  };

  return (
    <section id="pnl-calendar" className="relative border-t border-neutral-200/60 bg-white overflow-hidden" data-testid="landing-pnl-calendar">
      <div className="absolute -top-20 right-1/4 w-[420px] h-[420px] rounded-full bg-emerald-200/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 left-1/4 w-[420px] h-[420px] rounded-full bg-red-200/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
        {/* Copy */}
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold uppercase tracking-widest mb-4">
            <CalendarIcon size={11} weight="fill" /> P&L Calendar
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-neutral-900 leading-[1.05]">
            See your month at a glance. <span className="bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">Green days, red days, streaks.</span>
          </h2>
          <p className="mt-5 text-lg text-neutral-600 max-w-xl leading-relaxed">
            Every closed trade automatically fills the calendar with your daily P&L —
            colour-intensity shows conviction, weekends stay out of the way, and
            you spot revenge-trading Mondays before they cost you the month.
          </p>
          <ul className="mt-6 space-y-2.5 text-sm text-neutral-700">
            {[
              "Daily net P&L in dollars — no math on your side",
              "Colour heat scales with size, so 3× losers scream louder",
              "Hover any day to see the trades that made it",
              "Filter by strategy, symbol or account instantly",
            ].map(li => (
              <li key={li} className="flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-500 shrink-0" />
                {li}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/signup">
              <Button size="lg" className="rounded-full h-12 px-7 hover:bg-neutral-200 font-semibold" style={{ background: "#ffffff", color: "#0a0a0a" }} data-testid="pnlcal-cta">
                Log your first trade
              </Button>
            </Link>
            <a href="#features" className="inline-flex items-center h-12 px-4 text-sm text-white/80 hover:text-white font-medium">
              More features →
            </a>
          </div>
        </div>

        {/* Calendar card */}
        <div className="relative">
          <div className="rounded-3xl border border-neutral-200 bg-white shadow-[0_25px_60px_-15px_rgba(15,23,42,0.18)] p-4 sm:p-6 md:p-8" data-testid="pnl-calendar-card">
            {/* header row: month + summary chips */}
            <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">This month</p>
                <p className="font-display text-2xl font-bold text-neutral-900" data-testid="pnlcal-month-label">{monthName}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className={`rounded-xl px-3 py-2 ${total >= 0 ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
                  <p className="text-[9px] uppercase tracking-widest text-neutral-500">Net</p>
                  <p className={`font-mono-num text-lg font-black leading-tight ${total >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {total >= 0 ? "+" : "−"}${Math.abs(total).toLocaleString()}
                  </p>
                </div>
                <div className="rounded-xl px-3 py-2 bg-neutral-50 border border-neutral-200">
                  <p className="text-[9px] uppercase tracking-widest text-neutral-500">W / L</p>
                  <p className="font-mono-num text-lg font-black leading-tight text-neutral-900">{wins} / {losses}</p>
                </div>
              </div>
            </div>

            {/* Day-of-week header */}
            <div className="grid grid-cols-7 gap-1.5 mb-1.5">
              {DAY_LABELS.map((d, i) => (
                <div key={d} className={`text-center text-[10px] font-semibold uppercase tracking-widest py-1 ${i >= 5 ? "text-neutral-300" : "text-neutral-500"}`}>{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1 sm:gap-1.5" data-testid="pnl-calendar-grid">
              {days.map((v, i) => {
                const dayNum = dayNumFor(i);
                if (v === undefined || dayNum == null) return <div key={i} className="aspect-[5/4] rounded-lg bg-neutral-50/60" />;
                if (v === null) {
                  return (
                    <div key={i} className="aspect-[5/4] rounded-lg bg-neutral-100/60 border border-neutral-200/70 flex items-start justify-end p-1 sm:p-1.5">
                      <span className="text-[10px] sm:text-[11px] text-neutral-500 font-semibold" style={{ color: "#94a3b8" }}>{dayNum}</span>
                    </div>
                  );
                }
                const cls = toneClass(v);
                const strong = Math.abs(v) > 500;
                return (
                  <div key={i}
                    className={`aspect-[5/4] rounded-lg border px-1 sm:px-1.5 py-1 flex flex-col justify-between overflow-hidden ${cls} transition-transform hover:-translate-y-0.5 hover:shadow-md`}
                    data-testid={`pnl-day-${dayNum}`}>
                    <span className={`text-[9px] sm:text-[10px] font-semibold ${strong ? "opacity-80" : "opacity-70"}`}>{dayNum}</span>
                    <span className={`font-mono-num font-bold whitespace-nowrap leading-none tracking-tight ${strong ? "text-[10px] sm:text-[13px]" : "text-[9px] sm:text-[11px]"}`}>
                      {v > 0 ? "+" : "−"}${Math.abs(v)}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-5 flex items-center justify-between text-[11px] text-neutral-500 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="uppercase tracking-widest text-[9px]">Loss</span>
                <span className="w-4 h-3 rounded-sm bg-red-500 border border-red-600" />
                <span className="w-4 h-3 rounded-sm bg-red-100 border border-red-300" />
                <span className="w-4 h-3 rounded-sm bg-neutral-100 border border-neutral-200" />
                <span className="w-4 h-3 rounded-sm bg-emerald-100 border border-emerald-300" />
                <span className="w-4 h-3 rounded-sm bg-emerald-500 border border-emerald-600" />
                <span className="uppercase tracking-widest text-[9px]">Profit</span>
              </div>
              <span className="italic">Weekends muted · click any day for trades</span>
            </div>
          </div>

          {/* Floating call-out */}
          <div className="hidden md:flex absolute -top-5 -left-5 items-center gap-2 bg-white rounded-full shadow-lg border border-neutral-200 px-3.5 py-2" data-testid="pnlcal-streak-chip">
            <Fire />
            <span className="text-xs font-semibold text-neutral-900">5-day green streak</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Fire() {
  return (
    <span className="grid place-items-center w-6 h-6 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white text-[11px]">
      <TrendUp size={12} weight="fill" />
    </span>
  );
}

export default function Landing({ region = "global" } = {}) {
  // Region-aware SEO: /in, /us, /uk, /eu, /sea override title, description,
  // canonical + inject FAQPage / HowTo / Product JSON-LD so AI answer engines
  // (ChatGPT, Perplexity, Google AI Overviews, Gemini) can cite Tradelog
  // with structured answers. Global "/" gets the same schema stack too.
  const regionSeo = getRegionSeo(region);
  useSeo(regionSeo);
  useEffect(() => { applyRegionCurrency(region); }, [region]);
  useReveal();
  const [ccy] = useCurrency();
  const heroRef = useRef(null);
  const heroChartRef = useRef(null);
  useParallax(heroRef, heroChartRef, 18);
  return (
    <div className="min-h-screen bg-black text-white [--tj-star:rgba(255,255,255,.6)]" data-testid="landing-page">
      <CursorSparkles />
      {/* Global page-wide twinkling starfield — always behind content, above bg */}
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        {Array.from({ length: 30 }).map((_, i) => {
          const s = ((i * 71) % 100);
          return (
            <span
              key={i}
              className="absolute rounded-full motion-reduce:animate-none"
              style={{
                width: 1 + (s % 3), height: 1 + (s % 3),
                left: ((i * 143) % 100) + "%",
                top:  ((i * 79) % 100) + "%",
                background: s % 5 === 0 ? "#f5c443" : s % 3 === 0 ? "#5aa2ff" : "rgba(255,255,255,.6)",
                animation: `tj-twinkle ${3 + (s % 6)}s ease-in-out ${(s % 40) / 10}s infinite`,
                opacity: 0.35,
              }}
            />
          );
        })}
      </div>
      <style>{`
        @keyframes tj-twinkle { 0%,100% { opacity: .15 } 50% { opacity: 1 } }
        /* Nuke old light-mode surfaces on the landing so the dark theme flows
           everywhere without touching every section by hand. */
        [data-testid="landing-page"] { color: #e4e4e7 }
        [data-testid="landing-page"] .bg-white,
        [data-testid="landing-page"] .bg-white\\/60,
        [data-testid="landing-page"] .bg-white\\/70 { background: rgba(255,255,255,0.04) !important; backdrop-filter: blur(8px); border-color: rgba(255,255,255,0.08) !important }
        [data-testid="landing-page"] .text-neutral-900 { color: #f5f5f5 !important }
        [data-testid="landing-page"] .text-neutral-800 { color: #ececec !important }
        [data-testid="landing-page"] .text-neutral-700 { color: #d4d4d8 !important }
        [data-testid="landing-page"] .text-neutral-600 { color: #a1a1aa !important }
        [data-testid="landing-page"] .text-neutral-500 { color: #a1a1aa !important }
        [data-testid="landing-page"] .text-neutral-400 { color: #cbd5e1 !important }
        [data-testid="landing-page"] .border-neutral-200,
        [data-testid="landing-page"] .border-neutral-200\\/60,
        [data-testid="landing-page"] .border-neutral-300 { border-color: rgba(255,255,255,0.10) !important }
        [data-testid="landing-page"] .bg-neutral-100 { background: rgba(255,255,255,0.05) !important }
        [data-testid="landing-page"] .bg-neutral-50 { background: rgba(255,255,255,0.03) !important }
        /* Compare table — force readable text in every cell of the first column
           and header row so users can actually read the feature names. */
        [data-testid="landing-page"] table td,
        [data-testid="landing-page"] table th { color: #e4e4e7 !important }
        [data-testid="landing-page"] table td:first-child { color: #f5f5f5 !important; font-weight: 500 }
        [data-testid="landing-page"] section { position: relative }
      `}</style>
      <SectionNav />
      {/* nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-home">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
              <Lightning size={16} weight="fill" className="text-white" />
            </span>
            <span className="relative inline-block font-display text-xl font-bold tracking-tight text-white leading-none pt-2">
              <span
                className="absolute right-0 -top-1 text-[7px] font-semibold tracking-[0.15em] uppercase text-blue-300/90 whitespace-nowrap"
                style={{ letterSpacing: "0.08em" }}>
                by Yash Khajania
              </span>
              Tradelog
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-400">
            <a href="#prop-challenge" className="hover:text-white transition-colors">Prop Challenge</a>
            <a href="#metatrader" className="hover:text-white transition-colors">MT4 / MT5</a>
            <a href="#strategies" className="hover:text-white transition-colors">Strategies</a>
            <a href="#compare" className="hover:text-white transition-colors">Compare</a>
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <CurrencyPicker variant="nav" />
            <div className="hidden md:block w-[188px]">
              <GoogleSignInButton label="Sign in with Google" testId="nav-google-btn" />
            </div>
            <Link to="/login"><Button variant="ghost" className="text-neutral-300 hover:text-white hover:bg-white/10" data-testid="nav-login-btn">Login</Button></Link>
            <Link to="/signup">
              <Button className="rounded-full hover:bg-neutral-200 font-semibold" style={{ background: "#ffffff", color: "#0a0a0a" }} data-testid="nav-signup-btn">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO — cinematic dark canvas with animated light beams + tilted dashboard.
          Inspired by user's reference: "Your Trades Tell a Story. Are You Listening?" */}
      <CinematicHero />

      {/* Legacy secondary hero hidden — CinematicHero replaces it entirely */}
      <section ref={heroRef} className="hidden relative overflow-hidden" data-testid="hero-secondary">
        {/* Aurora — animated mesh gradient. Layered above the pale hero bg
            for a richer, smooth-motion feel without going full dark-mode. */}
        <div className="aurora" aria-hidden="true" />
        <div className="absolute inset-0 -z-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-blue-200/50 blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-[560px] h-[560px] rounded-full bg-pink-200/40 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: copy */}
          <div>
            <div data-reveal className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-900 text-white text-[10px] font-semibold uppercase tracking-widest mb-5" data-testid="hero-eyebrow">
              <Rocket size={11} weight="fill" className="text-primary" /> Now with Prop Firm Challenge
            </div>
            <h1 data-reveal data-delay="1" className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-neutral-900 leading-[1.05]" data-testid="hero-heading">
              Journal every trade.{" "}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Master prop firm rules.</span>{" "}
              Trade like a pro.
            </h1>
            <p data-reveal data-delay="2" className="mt-6 text-lg text-neutral-600 max-w-xl leading-relaxed" data-testid="hero-sub">
              Tradelog is a professional trading journal <b>and</b> a live paper-trading
              terminal that enforces the exact rules of FTMO, MyForexFunds, FundedNext,
              The5ers and more — so you can pass a real challenge instead of paying
              hundreds to fail one.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup">
                <Button size="lg" className="rounded-full h-12 px-8 bg-neutral-900 hover:bg-neutral-800 text-white text-base" data-testid="hero-cta">
                  Get Started
                </Button>
              </Link>
              <button
                type="button"
                onClick={() => {
                  const redirectUrl = window.location.origin + "/dashboard";
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                data-testid="hero-google-cta"
                className="h-12 px-6 rounded-full flex items-center gap-2.5 bg-white text-neutral-900 border border-neutral-300 hover:bg-neutral-100 transition-colors text-base font-semibold shadow-sm">
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                Continue with Google
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="font-semibold text-neutral-900">Excellent</span>
              <div className="flex items-center gap-0.5">
                {[0,1,2,3,4].map(i => <Star key={i} size={14} weight="fill" className="text-emerald-500" />)}
              </div>
              <span className="text-neutral-600">14-day free trial · No card required</span>
            </div>

            <div className="mt-10">
              <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-3">Everything in one place</p>
              <div className="flex flex-wrap gap-2">
                {["Detailed Journal", "Broker CSV Import", "P&L Calendar", "Discipline Score", "Chart Screenshots", "Sharable Reports"].map(t => (
                  <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white/60 px-3 py-1.5 text-xs font-medium text-neutral-700 backdrop-blur">
                    {t === "Detailed Journal" && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right: live animated candlestick chart with floating cards */}
          <div className="relative group" ref={heroChartRef}>
            <div className="hero-shake rounded-3xl border border-neutral-200/80 bg-neutral-950 shadow-[0_30px_80px_-20px_rgba(30,30,80,0.25)] overflow-hidden will-change-transform aspect-[900/520]" data-testid="hero-image-wrap">
              <HeroAnimatedChart />
            </div>

            {/* Floating card 1 — CSV import success */}
            <div className="hidden md:flex absolute -top-4 -left-6 lg:-left-10 items-center gap-3 bg-white rounded-2xl shadow-xl border border-neutral-200 px-4 py-3 w-[260px]"
                 data-testid="hero-toast-csv">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-emerald-100">
                <Upload size={16} weight="bold" className="text-emerald-600" />
              </span>
              <div className="text-xs">
                <p className="font-semibold text-neutral-900">CSV Import Successful</p>
                <p className="text-neutral-500 mt-0.5">47 trades added from Zerodha</p>
              </div>
              <span className="text-[10px] text-neutral-400 ml-auto">2s ago</span>
            </div>

            {/* Floating card 2 — Discipline Score */}
            <div className="hidden md:block absolute -bottom-6 -right-4 lg:-right-8 bg-white rounded-2xl shadow-xl border border-neutral-200 px-5 py-4 w-[240px]"
                 data-testid="hero-discipline-card">
              <div className="flex items-center justify-between">
                <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Discipline Score</p>
                <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">GREAT</span>
              </div>
              <p className="font-num text-4xl font-black text-neutral-900 mt-1.5">83</p>
              <div className="mt-2 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-500 via-emerald-500 to-emerald-400 rounded-full" style={{ width: "83%" }} />
              </div>
              <p className="text-[10px] text-neutral-500 mt-2">No over-trading days flagged this week.</p>
            </div>

            {/* Floating card 3 — Sharable link */}
            <div className="hidden lg:flex absolute top-1/3 -right-8 items-center gap-2 bg-white rounded-full shadow-xl border border-neutral-200 px-3 py-2"
                 data-testid="hero-toast-share">
              <Share size={14} weight="fill" className="text-indigo-500" />
              <span className="text-xs font-medium text-neutral-800">P&L link copied</span>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee ticker — smooth infinite scroll of tradable symbols */}
      <section className="relative border-y border-neutral-200/60 bg-neutral-950 text-white/80 py-6 overflow-hidden" data-testid="landing-ticker">
        <div className="marquee">
          <div className="marquee-track font-mono text-xs uppercase tracking-[0.35em]">
            {["BTCUSD · 63,927", "ETHUSD · 1,872", "XAUUSD · 4,043", "EURUSD · 1.0466", "USOIL · 72.5",
              "US30 · 42,470", "NAS100 · 21,180", "SPX500 · 5,796", "GBPUSD · 1.2412", "USDJPY · 155.2",
              "XAGUSD · 32.6", "AUDUSD · 0.6350"].concat([
              "BTCUSD · 63,927", "ETHUSD · 1,872", "XAUUSD · 4,043", "EURUSD · 1.0466", "USOIL · 72.5",
              "US30 · 42,470", "NAS100 · 21,180", "SPX500 · 5,796", "GBPUSD · 1.2412", "USDJPY · 155.2",
              "XAGUSD · 32.6", "AUDUSD · 0.6350"]).map((t, i) => (
                <span key={i} className="inline-flex items-center gap-2 whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {t}
                </span>
              ))}
          </div>
        </div>
      </section>

      {/* P&L CALENDAR SHOWCASE — see your month at a glance */}
      <PnlCalendarShowcase />

      {/* PROP CHALLENGE SHOWCASE — the star of the release */}
      <section id="prop-challenge" className="relative border-t border-neutral-200/60 bg-neutral-950 text-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)," +
              "linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }} />
        <div className="absolute -top-40 right-0 w-[720px] h-[720px] rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 left-0 w-[560px] h-[560px] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/20 border border-primary/30 text-primary text-[10px] font-semibold uppercase tracking-widest mb-4">
              <Rocket size={11} weight="fill" /> New · Tradelog Prop Firm Challenge
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Learn to trade under prop firm rules. <span className="text-primary">Zero real money.</span>
            </h2>
            <p className="mt-5 text-lg text-neutral-300 max-w-2xl">
              A full paper-trading terminal that mirrors FTMO, MyForexFunds, FundedNext, The5ers,
              Alpha Capital, Instant Funding, E8 and Topstep — with their exact rules enforced live.
              Blow the daily loss, breach the DD, miss the news lock — find out here, not with your $500 challenge fee.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup">
                <Button size="lg" className="rounded-full h-12 px-8 hover:bg-neutral-200 text-base font-semibold" style={{ background: "#ffffff", color: "#0a0a0a" }} data-testid="propsim-hero-cta">
                  <Rocket size={18} weight="fill" className="mr-1.5" /> Start free challenge
                </Button>
              </Link>
              <a href="#prop-challenge-features">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-8 border-white/20 bg-white/5 text-white hover:bg-white/10 text-base">
                  See what's enforced
                </Button>
              </a>
            </div>
          </div>

          {/* Terminal mockup */}
          <div className="mt-14 rounded-2xl border border-white/10 bg-black/60 overflow-hidden shadow-2xl">
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/10 bg-black/60">
              <div className="flex items-center gap-1.5">
                <Lightning size={14} weight="fill" className="text-primary" />
                <span className="font-display font-bold text-sm">Tradelog Trade Desk</span>
                <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] tracking-widest uppercase bg-primary/20 text-primary font-semibold">DEMO</span>
              </div>
              <div className="ml-auto text-[10px] uppercase tracking-widest text-white/40 font-mono">Virtual money · No real orders</div>
            </div>
            <div className="flex gap-0.5 px-2 py-2 border-b border-white/10 overflow-x-auto hide-scroll">
              {[
                ["🥇","XAUUSD"],["₿","BTCUSD"],["Ξ","ETHUSD"],["🇪🇺","EURUSD"],["🇬🇧","GBPUSD"],
                ["🇯🇵","USDJPY"],["🇨🇦","USDCAD"],["🛢️","USOIL"],["🇦🇺","AUDUSD"],["🥈","XAGUSD"],["🇦🇺","AUDCAD"],
              ].map(([f, s], i) => (
                <div key={s} className={`shrink-0 flex items-center gap-1.5 px-2.5 h-8 rounded-md text-xs font-semibold ${i === 3 ? "bg-white/10 text-white ring-1 ring-white/20" : "text-neutral-400"}`}>
                  <span>{f}</span><span className="font-mono">{s}</span>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-3">
              <div className="lg:col-span-2 p-8 min-h-[300px] relative"
                style={{
                  background:
                    "radial-gradient(60% 80% at 30% 30%, rgba(16,185,129,0.10) 0%, transparent 60%)," +
                    "radial-gradient(60% 80% at 80% 70%, rgba(239,68,68,0.06) 0%, transparent 60%)," +
                    "#0a0a0a",
                }}>
                <div className="text-[10px] tracking-widest uppercase text-neutral-500">Euro / US Dollar · 1h</div>
                <div className="mt-1 font-mono-num text-3xl font-bold">1.08432 <span className="text-emerald-400 text-sm">+0.24%</span></div>
                <svg viewBox="0 0 500 160" className="mt-6 w-full">
                  <path d="M0,120 L40,110 L80,115 L120,90 L160,95 L200,70 L240,80 L280,55 L320,60 L360,40 L400,45 L440,30 L500,35"
                    fill="none" stroke="#10b981" strokeWidth="2" />
                  <path d="M0,120 L40,110 L80,115 L120,90 L160,95 L200,70 L240,80 L280,55 L320,60 L360,40 L400,45 L440,30 L500,35 L500,160 L0,160 Z"
                    fill="url(#g1)" opacity="0.3" />
                  <defs>
                    <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white/40 text-[10px] font-mono tracking-widest uppercase">
                  <Lightning size={10} weight="fill" /> Tradelog · Prop Challenge
                </div>
              </div>
              <div className="border-l border-white/10 p-5">
                <p className="text-[10px] tracking-widest uppercase text-neutral-500">Place order · EURUSD</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button className="h-24 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white text-left px-4 py-3">
                    <p className="text-[10px] uppercase tracking-[0.2em] opacity-70">Sell</p>
                    <p className="font-mono-num font-black text-3xl mt-0.5">1.08430</p>
                  </button>
                  <button className="h-24 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white text-right px-4 py-3">
                    <p className="text-[10px] uppercase tracking-[0.2em] opacity-70">Buy</p>
                    <p className="font-mono-num font-black text-3xl mt-0.5">1.08432</p>
                  </button>
                </div>
                <div className="mt-4 space-y-1 text-[11px] font-mono text-neutral-400">
                  <div className="flex justify-between"><span>Volume</span><span>0.10 lots</span></div>
                  <div className="flex justify-between"><span>Fees</span><span>≈ $3.00</span></div>
                  <div className="flex justify-between"><span>Leverage</span><span>1:500</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* What's enforced */}
          <div id="prop-challenge-features" className="mt-16 grid md:grid-cols-3 gap-4 tilt-parent">
            {[
              { icon: Target,          title: "Profit target",   desc: "Track your % return vs the exact target of the tier you picked. Live progress bar." },
              { icon: TrendDown,       title: "Max drawdown",    desc: "Static or trailing HWM DD — whichever the firm uses. Breach and it's over, just like the real challenge." },
              { icon: LockKey,         title: "News trading lock", desc: "High-impact events auto-block orders ±2 minutes across the affected currencies." },
              { icon: Scales,          title: "Consistency rule",  desc: "Best-day > 30% of profit? Target hit but PASS blocked — same as FTMO / MFF / FundedNext." },
              { icon: CalendarIcon,    title: "Min trading days", desc: "Auto-count distinct trading days. Won't flip to PASSED until you cover the minimum." },
              { icon: Coins,           title: "Payout projection", desc: "Once you pass, see the realistic monthly & yearly take at the firm's exact split." },
              { icon: Ranking,         title: "Community leaderboard", desc: "Anonymised ladder of who's currently passing which challenge — race the room." },
              { icon: Trophy,          title: "Certificate PDF", desc: "Every PASS auto-generates a shareable landscape certificate ready for X / LinkedIn." },
              { icon: Share,           title: "Public P&L card", desc: "One-tap share link + QR code for your challenge run. Post it, or gate it — your call." },
            ].map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="tilt rounded-xl border border-white/10 bg-white/[0.03] p-5 hover:bg-white/[0.06] transition-colors" data-testid={`propsim-lp-feature-${i}`}>
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/20 border border-primary/30 mb-3">
                  <Icon size={18} weight="fill" className="text-primary" />
                </span>
                <h3 className="font-display text-base font-bold">{title}</h3>
                <p className="text-sm text-neutral-400 mt-1 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MetaTrader + Strategy showcase — dark blue glass cards to match the
          rest of the cinematic landing. (Previous light gradient cards were
          being force-inverted by the landing's global dark-theme overrides,
          which left the headings unreadable.) */}
      <section id="metatrader" className="max-w-7xl mx-auto px-6 py-24 border-t border-white/10">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10">
          {/* MT4/MT5 One-click import */}
          <div className="rounded-2xl border border-blue-400/20 p-6 sm:p-8 relative overflow-hidden"
               style={{
                 background: "linear-gradient(155deg, rgba(30,58,138,0.55) 0%, rgba(15,23,42,0.85) 55%, rgba(8,12,28,0.95) 100%)",
                 boxShadow: "0 30px 80px -20px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
               }}>
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-blue-500/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-200 text-[10px] font-semibold uppercase tracking-widest mb-4">
                <Lightning size={11} weight="fill" /> One-click import
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white leading-tight">
                Drop your MT4 / MT5 statement. Done.
              </h3>
              <p className="mt-3 text-neutral-300 leading-relaxed text-sm sm:text-base">
                No FTP, no EA install, no column mapping. Save your MetaTrader account history as HTML,
                drop it into Tradelog — every closed trade lands in seconds, correctly de-duplicated.
              </p>
              <div className="mt-6 rounded-xl border-2 border-dashed border-blue-400/30 p-6 bg-white/[0.03] text-center">
                <Upload size={28} className="mx-auto text-blue-300 mb-2" weight="duotone" />
                <p className="text-sm font-semibold text-white">Drop your MT4/MT5 statement here</p>
                <p className="text-xs text-neutral-400 mt-1">
                  Auto-detects broker, currency, and every closed ticket.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {["FTMO","MyForexFunds","FundedNext","The5ers","E8","Exness","IC Markets","XM","Pepperstone","OANDA"].map(b => (
                  <span key={b} className="text-[11px] px-2 py-1 rounded-full bg-white/[0.06] border border-white/10 text-neutral-200 font-medium">{b}</span>
                ))}
                <span className="text-[11px] px-2 py-1 rounded-full bg-blue-500/90 text-white font-semibold">+ every MT4/MT5 broker</span>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-neutral-400">
                <span className="flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-400" /> Auto-create account named after the broker</span>
                <span className="flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-400" /> Idempotent re-drop</span>
              </div>
            </div>
          </div>

          {/* Strategy Report Cards */}
          <div id="strategies" className="rounded-2xl border border-emerald-400/20 p-6 sm:p-8 relative overflow-hidden"
               style={{
                 background: "linear-gradient(155deg, rgba(6,78,59,0.45) 0%, rgba(15,23,42,0.85) 55%, rgba(8,12,28,0.95) 100%)",
                 boxShadow: "0 30px 80px -20px rgba(16,185,129,0.28), inset 0 1px 0 rgba(255,255,255,0.06)",
               }}>
            <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 text-[10px] font-semibold uppercase tracking-widest mb-4">
                <Target size={11} weight="fill" /> Strategy report cards
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white leading-tight">
                Which strategy actually pays you?
              </h3>
              <p className="mt-3 text-neutral-300 leading-relaxed text-sm sm:text-base">
                Tag your trades — <em className="text-emerald-200 not-italic font-semibold">breakout, scalp, news, swing</em> — and Tradelog turns each tag
                into a full accuracy report: win rate, profit factor, expectancy, avg R:R, best/worst
                symbol, plus a public share link and PDF export.
              </p>
              <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/25 border border-emerald-400/40 text-emerald-200 font-bold uppercase tracking-widest">breakout</span>
                    <span className="text-[10px] text-neutral-400">12 trades</span>
                  </div>
                  <TrendUp size={18} weight="fill" className="text-emerald-400" />
                </div>
                <p className="font-mono-num text-3xl font-black text-emerald-300 mt-2">+$4,280</p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                  <div><p className="text-neutral-400">Win rate</p><p className="font-mono-num font-bold text-white text-base mt-0.5">67%</p></div>
                  <div><p className="text-neutral-400">Profit factor</p><p className="font-mono-num font-bold text-white text-base mt-0.5">2.4</p></div>
                  <div><p className="text-neutral-400">Expectancy</p><p className="font-mono-num font-bold text-emerald-300 text-base mt-0.5">+$357</p></div>
                  <div><p className="text-neutral-400">Avg R:R</p><p className="font-mono-num font-bold text-white text-base mt-0.5">1.8</p></div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1.5 text-[11px] text-neutral-400">
                <span className="flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-400" /> Public share link</span>
                <span className="flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-400" /> PDF export</span>
                <span className="flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-400" /> Multi-tag per trade</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom row: 3 quick-highlight cards */}
        <div className="mt-6 sm:mt-8 grid md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur p-5">
            <MegaphoneSimple size={22} weight="fill" className="text-amber-400 mb-2" />
            <h4 className="font-display text-base font-bold text-white">News lock enforced live</h4>
            <p className="text-sm text-neutral-300 mt-1">High-impact events across every currency you're trading, auto-locking your order button 2 min before + 2 min after — just like a real prop firm.</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur p-5">
            <Scales size={22} weight="fill" className="text-blue-400 mb-2" />
            <h4 className="font-display text-base font-bold text-white">Consistency rule tracker</h4>
            <p className="text-sm text-neutral-300 mt-1">FTMO 30% · MFF 25% · FundedNext 40% — Tradelog watches your best-day share and blocks the PASS until you spread the wins.</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur p-5">
            <GraduationCap size={22} weight="fill" className="text-emerald-400 mb-2" />
            <h4 className="font-display text-base font-bold text-white">Guided first-trade tour</h4>
            <p className="text-sm text-neutral-300 mt-1">A 30-second walkthrough runs the first time you land — you'll place a paper trade with confidence in under a minute.</p>
          </div>
        </div>
      </section>

      {/* features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[10px] tracking-[0.3em] uppercase text-indigo-600 font-semibold">Every trade, every insight</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-3 text-neutral-900">Everything you need to review, refine and repeat.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 tilt-parent">
          {[
            { icon: ChartLineUp, title: "Day / Week / Month / Year analytics", desc: "Return %, win rate, best day, growth curve — computed automatically for every period." },
            { icon: Upload, title: "Broker CSV import", desc: "Bulk import from Zerodha, Upstox, Groww, Angel One, Fyers, Exness — with smart column mapping you review before saving." },
            { icon: Plugs, title: "Auto broker sync", desc: "Connect Binance, CoinDCX and Delta once with encrypted API keys. Closed trades stream into your journal automatically every night — no CSV needed." },
            { icon: FileArrowDown, title: "MT4 / MT5 statement import", desc: "Drop the HTML or CSV statement from XM, Vantage, Exness or any MetaTrader broker. We auto-detect every closed ticket, symbol and currency in one click." },
            { icon: CalendarIcon, title: "P&L Calendar view", desc: "See your daily P&L on a monthly grid at a glance. Spot streaks and drawdown days instantly." },
            { icon: CheckSquare, title: "Discipline Score", desc: "Track over-trading, revenge trades, missed exits, and rule-breaks per week. The higher the score, the cleaner the process." },
            { icon: Camera, title: "Chart screenshots & trade logic", desc: "Attach before/after screenshots, jot down setup notes and thesis on every trade." },
            { icon: MagnifyingGlass, title: "Find any specific trade", desc: "Search by symbol, notes, tag, side, or date range. Every entry is one click away." },
            { icon: Share, title: "Sharable P&L links", desc: "Create a public link with your equity curve. Perfect for prop-firm applications or sharing with friends." },
            { icon: Notepad, title: "Print & PDF reports", desc: "Download PDF reports for any period or print your dashboard directly from the browser." },
            { icon: TrendUp, title: "Live charts + events", desc: "TradingView-powered charts and a countdown to the next high-impact economic event." },
          ].map(({ icon: Icon, title, desc }, i) => (
            <div key={i} className="tilt rounded-2xl border border-neutral-200 bg-white p-6 hover:border-indigo-200 transition-colors duration-200">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-blue-100 mb-4">
                <Icon size={20} weight="duotone" className="text-indigo-600" />
              </span>
              <h3 className="font-display text-base font-bold text-neutral-900">{title}</h3>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARISON — Tradelog vs the field */}
      <section id="compare" className="max-w-7xl mx-auto px-6 py-24 border-t border-neutral-200/60">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-[10px] tracking-[0.3em] uppercase text-indigo-600 font-semibold">How we compare</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-3 text-neutral-900">
            The only journal built for prop-firm traders.
          </h2>
          <p className="mt-4 text-neutral-600">
            Most trading journals log trades. Tradelog does that <em>and</em> puts you in a full
            paper prop-firm challenge with the exact rules every funded trader has to survive.
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-neutral-200 bg-white shadow-sm">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead>
              <tr className="text-left border-b border-neutral-200">
                <th className="px-6 py-4 text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-semibold">Feature</th>
                <th className="px-4 py-4 text-center" data-testid="compare-th-tradelog"
                    style={{ background: "linear-gradient(180deg, rgba(59,130,246,0.18), rgba(59,130,246,0.05))", borderTop: "2px solid #5aa2ff" }}>
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="grid place-items-center w-6 h-6 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 shrink-0">
                      <Lightning size={12} weight="fill" className="text-white" />
                    </span>
                    <span className="font-display font-bold" style={{ color: "#ffffff" }}>Tradelog</span>
                  </div>
                  <div className="text-[10px] mt-1 font-semibold" style={{ color: "#5aa2ff" }}>{formatPrice(600, ccy)} / mo</div>
                </th>
                <th className="px-4 py-4 text-center">
                  <p className="font-semibold text-neutral-900">Edgewonk</p>
                  <p className="text-[10px] text-neutral-500 mt-0.5">$169 / year</p>
                </th>
                <th className="px-4 py-4 text-center">
                  <p className="font-semibold text-neutral-900">Tradezella</p>
                  <p className="text-[10px] text-neutral-500 mt-0.5">$29 / mo</p>
                </th>
                <th className="px-4 py-4 text-center">
                  <p className="font-semibold text-neutral-900">TraderVue</p>
                  <p className="text-[10px] text-neutral-500 mt-0.5">$29 / mo</p>
                </th>
                <th className="px-4 py-4 text-center">
                  <p className="font-semibold text-neutral-900">MyFxBook</p>
                  <p className="text-[10px] text-neutral-500 mt-0.5">Free</p>
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                { f: "Paper prop-firm simulator",   t: "yes", e: "no", z: "no", v: "no", m: "no" },
                { f: "Prop rules enforced live (DD, daily, min days)", t: "yes", e: "no", z: "no", v: "no", m: "no" },
                { f: "High-impact news trading lock", t: "yes", e: "no", z: "no", v: "no", m: "no" },
                { f: "Consistency rule check (30%)",  t: "yes", e: "no", z: "no", v: "no", m: "no" },
                { f: "Payout simulator per firm split", t: "yes", e: "no", z: "no", v: "no", m: "no" },
                { f: "Certificate PDF on PASS",       t: "yes", e: "no", z: "no", v: "no", m: "no" },
                { f: "Community leaderboard",         t: "yes", e: "no", z: "no", v: "partial", m: "yes" },
                { f: "One-click MT4/MT5 statement import", t: "yes", e: "yes", z: "yes", v: "yes", m: "yes" },
                { f: "Auto broker sync (Binance / CoinDCX / Delta)", t: "yes", e: "no", z: "partial", v: "no", m: "partial" },
                { f: "Encrypted API keys (Fernet at rest)", t: "yes", e: "no", z: "no", v: "no", m: "no" },
                { f: "Strategy report cards + PDF",   t: "yes", e: "partial", z: "yes", v: "partial", m: "no" },
                { f: "Public P&L share card + QR",    t: "yes", e: "no", z: "yes", v: "yes", m: "yes" },
                { f: "Live TradingView charts inside", t: "yes", e: "no", z: "partial", v: "partial", m: "yes" },
                { f: "Broker CSV import (India + global)", t: "yes", e: "yes", z: "yes", v: "yes", m: "partial" },
                { f: "Trade screenshots & notes",     t: "yes", e: "yes", z: "yes", v: "yes", m: "partial" },
                { f: "Discipline / rule-break score", t: "yes", e: "yes", z: "no", v: "no", m: "no" },
                { f: "Prop firm rules tracker (real accts)", t: "yes", e: "no", z: "no", v: "no", m: "no" },
                { f: "First-trade guided tour",       t: "yes", e: "no", z: "no", v: "no", m: "no" },
              ].map((r, i) => (
                <tr key={i} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                  <td className="px-6 py-3.5 text-neutral-800">{r.f}</td>
                  <td className="px-4 py-3.5 text-center bg-indigo-50/30"><Cell v={r.t} highlight /></td>
                  <td className="px-4 py-3.5 text-center"><Cell v={r.e} /></td>
                  <td className="px-4 py-3.5 text-center"><Cell v={r.z} /></td>
                  <td className="px-4 py-3.5 text-center"><Cell v={r.v} /></td>
                  <td className="px-4 py-3.5 text-center"><Cell v={r.m} /></td>
                </tr>
              ))}
              <tr className="bg-neutral-50/60">
                <td className="px-6 py-4 font-semibold text-neutral-900">Total unique features</td>
                <td className="px-4 py-4 text-center bg-indigo-100/50">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-500 text-white text-xs font-bold">18 / 18</span>
                </td>
                <td className="px-4 py-4 text-center text-neutral-600 font-mono">6 / 18</td>
                <td className="px-4 py-4 text-center text-neutral-600 font-mono">6 / 18</td>
                <td className="px-4 py-4 text-center text-neutral-600 font-mono">6 / 18</td>
                <td className="px-4 py-4 text-center text-neutral-600 font-mono">6 / 18</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Why Tradelog wins — 3 pillars */}
        <div className="mt-10 grid md:grid-cols-3 gap-4 tilt-parent">
          {[
            {
              icon: Rocket,
              tone: "indigo",
              title: "Practice before you pay",
              body: "Every $500 challenge you blow is $500 you don't get back. Tradelog lets you fail the challenge here, adjust, then attempt it for real.",
            },
            {
              icon: Coins,
              tone: "emerald",
              title: "Prop-firm-native, not bolted on",
              body: "News lock, consistency, HWM drawdown, min days, per-firm splits — built in, not a settings page you have to configure.",
            },
            {
              icon: Lightning,
              tone: "amber",
              title: "1/4 the price of the alternatives",
              body: `${formatPrice(600, ccy)}/mo. Same feature set as $29/mo journals — plus everything they don't have. Priced for traders worldwide.`,
            },
          ].map(({ icon: Icon, tone, title, body }, i) => (
            <div key={i} className="tilt rounded-2xl border border-neutral-200 bg-white p-6 transition-colors">
              <span className={`grid place-items-center w-10 h-10 rounded-xl mb-3 ${
                tone === "indigo" ? "bg-indigo-100 text-indigo-600"
                : tone === "emerald" ? "bg-emerald-100 text-emerald-600"
                : "bg-amber-100 text-amber-600"
              }`}>
                <Icon size={20} weight="fill" />
              </span>
              <h3 className="font-display text-lg font-bold text-neutral-900">{title}</h3>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 rounded-2xl bg-neutral-900 text-white p-8 sm:p-10 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
              Ready to trade like a funded trader?
            </h3>
            <p className="text-neutral-300 mt-2 text-sm max-w-xl">
              14-day free trial. Full Prop Firm Challenge, MT4/MT5 import, and strategy reports —
              no credit card required.
            </p>
          </div>
          <Link to="/signup">
            <Button size="lg" className="rounded-full h-12 px-8 hover:bg-neutral-200 text-base font-semibold" style={{ background: "#ffffff", color: "#0a0a0a" }} data-testid="compare-cta">
              Start free trial
            </Button>
          </Link>
        </div>

        <p className="mt-6 text-[10px] text-center text-neutral-400">
          Comparison data based on publicly-listed features at the time of publication. All trademarks belong to their respective owners.
        </p>
      </section>

      {/* pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24 border-t border-neutral-200/60">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-[10px] tracking-[0.3em] uppercase text-indigo-600 font-semibold">Pricing</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-3 text-neutral-900">Simple, honest pricing</h2>
          <p className="mt-4 text-neutral-600">Start with a 14-day free trial. No card required. Cancel anytime.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {plans.map(p => (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
              }}
              className={`plan-card group relative rounded-2xl p-6 overflow-hidden transition-transform duration-300 hover:-translate-y-1 ${
                p.pop
                  ? "bg-neutral-900 text-white ring-2 ring-blue-500/60 shadow-2xl scale-[1.02]"
                  : "bg-white border border-neutral-200"
              }`}
              style={{ "--mx": "50%", "--my": "50%" }}>
              {/* Cursor-following spotlight glow — desktop hover + mobile touch */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-300"
                style={{
                  background: `radial-gradient(220px circle at var(--mx) var(--my), ${p.pop ? "rgba(90,162,255,0.35)" : "rgba(90,162,255,0.28)"}, transparent 60%)`,
                }} />
              {/* Animated gradient border on hover */}
              <span aria-hidden="true"
                className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: "conic-gradient(from 180deg at 50% 50%, #5aa2ff, #a78bfa, #f5c443, #5aa2ff)",
                  padding: "1px",
                  WebkitMask: "linear-gradient(#000,#000) content-box, linear-gradient(#000,#000)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                }} />
              <div className="relative z-10">
              {p.pop && (
                <span className="absolute -top-3 left-4 z-20 text-[10px] tracking-[0.2em] uppercase bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-3 py-1 rounded-full shadow-lg">
                  Most Popular
                </span>
              )}
              <h3 className="font-display text-2xl font-bold">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                <span className="font-num text-4xl sm:text-5xl font-black">{formatPrice(p.inr, ccy)}</span>
                <span className={`text-sm ${p.pop ? "text-neutral-400" : "text-neutral-500"}`}>{p.per}</span>
              </div>
              {p.savingsInr > 0 && (
                <p className={`mt-1 text-xs font-semibold ${p.pop ? "text-emerald-300" : "text-emerald-500"}`}>
                  Save {formatPrice(p.savingsInr, ccy)}
                </p>
              )}
              <ul className="mt-6 space-y-3">
                {p.features.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <CheckCircle size={18} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
                    <span className={p.pop ? "text-neutral-100" : "text-neutral-700"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup" className="block mt-8">
                <Button
                  className={`w-full rounded-full font-semibold ${p.pop ? "hover:bg-neutral-200" : "bg-neutral-900 text-white hover:bg-neutral-800"}`}
                  style={p.pop ? { background: "#ffffff", color: "#0a0a0a" } : undefined}
                  data-testid={`plan-cta-${p.id}`}>
                  Start free trial
                </Button>
              </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-neutral-200 py-8">
        <div className="max-w-7xl mx-auto px-6 text-xs text-neutral-500 flex flex-col md:flex-row items-center justify-between gap-2">
          <p>© {new Date().getFullYear()} Tradelog. Journal like a pro.</p>
          <p>Traders worldwide · Live currency preview · Razorpay secured</p>
        </div>
      </footer>
    </div>
  );
}

